import React, {useState, useEffect, useContext} from "react";

import * as ApiCalls from "./ApiCalls.js";

import {azureTokenContext, userIdContext} from "../../App";
import MissingSubIdsModal from "./MissingSubIdsModal";
import NewTaskModal from "./NewTaskModal";
import Loading from "./Loading";

import "../../style/responsibilityAreaTable.css";
import {targetUserIdContext} from "../MainPart";
import SelectTargetUser from "./PriorityTableComponents/SelectTargetUser";

const ResponsibilityAreaTable = () => {
    const azureToken = useContext(azureTokenContext);
    const userId = useContext(userIdContext);
    const [targetUserId] = useContext(targetUserIdContext);
    const [rows, setRows] = useState<ResponsibleAreaResponseType>();
    const [refreshResponsibleArea, setRefreshResponsibleArea] = useState(false);
    const [missingSubidsCollection, setMissingSubidsCollection] = useState<MissingSubidsCollectionType[]>([]);

    const [loadingStatus, setLoadingStatus] = useState("loading");

    useEffect(() => {
        setLoadingStatus("loading");
        ApiCalls.getResponsibleArea(targetUserId, azureToken)
            .then((res: ResponsibleAreaResponseType) => {
                if (res === undefined || res === null) {
                    setLoadingStatus("error");
                    return;
                }

                // Create a collection of unassigned sub-IDs
                const missingSubIdsCollection = makeMissingSubidsCollection(res, targetUserId);
                setMissingSubidsCollection(missingSubIdsCollection);

                let completedResponse = res;
                // If there are no work items, we still want to show the master documents
                for (const missingItem of missingSubIdsCollection) {
                    if (!missingItem.ToCs) continue; // Skip if ToCs is undefined
                    let lastWIIndex = findLastWorkItemIndex(completedResponse.WorkItems, missingItem.DocumentId, missingItem.Source);
                    if (lastWIIndex === -1) {
                        lastWIIndex = completedResponse.WorkItems.length;
                    }
                    completedResponse.WorkItems.splice(lastWIIndex, 0, {
                        Id: "",
                        OrigId: "",
                        SyncId: "",
                        DocumentId: missingItem.DocumentId,
                        DocumentTitle: missingItem.ToCs?.Title ?? "",
                        Version: missingItem.Version,
                        Source: missingItem.Source,
                        FromId: null,
                        Status: "",
                        AuthorEmail: "",
                        AuthorName: missingItem.MissingSubIds[0].AuthorId,
                        SharepointPath: "",
                        SharepointFolder: "",
                        UnknownContentPath: null,
                        UpdatedBy: null,
                        AdminUpdatedBy: "",
                        Created: "",
                        Updated: "",
                        SharedWith: [],
                        SubIds: missingItem.MissingSubIds,
                        LegalFieldId: "",
                        AuthorId: targetUserId,
                        BackupPath: null,
                        KarnovEditorId: "",
                        DueDate: "",
                        IsAcademicReview: false,
                        ContentReviewer: {Id: "", DueDate: null, ReviewedDate: null, EnableOnRevision: null},
                        ProofReader: {Id: "", DueDate: null, ReviewedDate: null, EnableOnRevision: null},
                        EUReviewer: {Id: "", DueDate: null, ReviewedDate: null, EnableOnRevision: null},
                        KarnovReviewer: {Id: "", DueDate: null, ReviewedDate: null, EnableOnRevision: null},
                        IsEUReviewable: false,
                        ReadyForReviewDate: null,
                        ReadyForPublishDate: null,
                        PublishedDate: null,
                        DeliveredDate: null,
                        IsRevision: false,
                        IsAuthorValidated: false,
                        IsVersionUpgrade: false,
                        IsDevOps: false,
                        ToCs: missingItem.ToCs,
                        MissingSubIds: missingItem.MissingSubIds,
                    });
                }

                setRows(completedResponse);

                setLoadingStatus("done");
                log("INFO", "Success getResponsibleArea", "AuthorsPortal", "", "");
                logMetric("AuthorsPortal", "", "Success", "", "Successful getResponsibleArea", "", "");
            })
            .catch(() => {
                setLoadingStatus("error");
                log("ERROR", "Failed getPrioritizedTasks", "AuthorsPortal", "", "");
                logMetric("AuthorsPortal", "", "Failed", "", "Failed getPrioritizedTasks in AuthorsPortal", "", "");
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, targetUserId, refreshResponsibleArea]);

    const log = (level, message, method, workItemId, userRole) => {
        ApiCalls.log(level, message, method, workItemId, userRole, userId, azureToken);
    };

    const logMetric = (category, subCategory, status, tag, message, workItemId, userRole) => {
        ApiCalls.logMetric(category, subCategory, status, tag, message, workItemId, userRole, userId, azureToken);
    };

    return (
        <>
            <>{loadingStatus === "loading" && <Loading/>}</>
            <>
                {loadingStatus === "done" && (
                    <>
                        <div style={{
                            margin: "2rem 0 0 4rem",
                        }}>
                            <SelectTargetUser/>
                        </div>
                        <div className="ra-table-container">
                            <div className="top-of-ra-table-container">
                                <div className="left-aligned-ra-container">
                                    <h1>Ansvarsområde</h1>
                                </div>
                            </div>
                            <table className="ra-table">
                                <thead>
                                <tr>
                                    <th className="ResponsibilityAreaTable-Name">Publikasjon</th>
                                    <th className="ResponsibilityAreaTable-Type">Type oppgave</th>
                                    <th className="ResponsibilityAreaTable-NewTask">Lag ny oppgave</th>
                                    <th className="ResponsibilityAreaTable-Status">Status</th>
                                    <th className="ResponsibilityAreaTable-DueDate">Frist</th>
                                    <th className="ResponsibilityAreaTable-Edit">Redigere</th>
                                    <th className="ResponsibilityAreaTable-MissingParts">Legg til bestemmelse</th>
                                </tr>
                                </thead>
                                {rows && rows.WorkItems.length === 0 && rows.MasterDocuments.length === 0 && (
                                    <tbody>
                                    <tr>
                                        <td colSpan={7}>
                                            <p style={{marginLeft: "30rem", fontSize: "1.5rem"}}>Ingenting å vise</p>
                                        </td>
                                    </tr>
                                    </tbody>
                                )}
                                {rows && (rows.WorkItems.length > 0 || rows.MasterDocuments.length > 0) && (
                                    <tbody>
                                    {rows &&
                                        rows.WorkItems.map((row, index) => (
                                            <tr key={index}>
                                                <td className="ResponsibilityAreaTable-Name">{row.DocumentTitle}</td>
                                                <td className="ResponsibilityAreaTable-Type">
                                                    {row.Id
                                                        ? row.IsAuthorValidated
                                                            ? "Hovedrevisjon"
                                                            : row.IsRevision
                                                                ? "Revisjon"
                                                                : "Nyskriving"
                                                        : "Ingen oppgave"}
                                                </td>
                                                <td className="ResponsibilityAreaTable-NewTask">
                                                    {rows && !row.Id && (
                                                        <NewTaskModal
                                                            newTask={row}
                                                            refreshResponsibleArea={refreshResponsibleArea}
                                                            setRefreshResponsibleArea={setRefreshResponsibleArea}
                                                        />
                                                    )}
                                                </td>
                                                <td className="ResponsibilityAreaTable-Status">{row.Status ? row.Status : ""}</td>
                                                <td className="ResponsibilityAreaTable-DueDate">
                                                    {row.DueDate ? new Date(row.DueDate).toLocaleDateString("no-NO") : ""}
                                                </td>
                                                <td className="ResponsibilityAreaTable-Edit">
                                                    {row.Id !== null && row.Id !== undefined && row.SharepointPath && row.SharepointFolder ? (
                                                        <>
                                                            <div>
                                                                <a href={row.SharepointPath} target="_blank"
                                                                   rel="noopener noreferrer">
                                                                    Åpne i online
                                                                </a>
                                                            </div>
                                                            <div>
                                                                <a href={getOpenInWordUrl(row.SharepointPath, row.SharepointFolder)}>Åpne
                                                                    i word</a>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        ""
                                                    )}
                                                </td>
                                                <td className="ResponsibilityAreaTable-MissingParts">
                                                    {rows && row.Id && (
                                                        <MissingSubIdsModal
                                                            workItem={row}
                                                            allMDsWIs={rows}
                                                            missingSubIdsCollection={missingSubidsCollection}
                                                            refreshResponsibleArea={refreshResponsibleArea}
                                                            setRefreshResponsibleArea={setRefreshResponsibleArea}
                                                        />
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                )}
                            </table>
                        </div>
                    </>

                )}
            </>
            <>{loadingStatus === "error" && "error"}</>
        </>
    );
};

export default ResponsibilityAreaTable;

function makeMissingSubidsCollection(res: ResponsibleAreaResponseType, userId: string): MissingSubidsCollectionType[] {
    const collection: MissingSubidsCollectionType[] = [];
    if (!res || !res.MasterDocuments || res.MasterDocuments.length === 0) {
        return collection;
    }

    res.MasterDocuments.forEach((md) => {
        const missingSubIds: MasterDocumentSubId[] = [];
        md.SubIds?.forEach((subId) => {
            if (subId.AuthorId === userId && !res.WorkItems.some((wi) => wi.SubIds?.some((s) => s.SubId === subId.SubId))) {
                missingSubIds.push(subId);
            }
        });

        if (missingSubIds.length > 0) {
            collection.push({
                DocumentId: md.DocumentId,
                Source: md.Source,
                Version: md.Version,
                LatestVersion: md.LatestVersion,
                ToCs: md.ToCs,
                MissingSubIds: missingSubIds,
            });
        }
    });
    return collection;
}

function getOpenInWordUrl(sharepointPath, sharepointFolder, postFixFolder = "") {
    const fileName = sharepointPath
        .split("&")
        .find((param) => param.includes("file="))
        ?.replace("file=", "");

    if (!fileName) return undefined;

    if (!postFixFolder || postFixFolder.trim() === "") {
        return `ms-word:ofe|u|${sharepointFolder}/${fileName}`;
    } else {
        return `ms-word:ofe|u|${sharepointFolder}/${postFixFolder}/${fileName}`;
    }
}

function findLastWorkItemIndex(workItems: WorkItem[], documentId: string, source: string): number {
    for (let i = workItems.length - 1; i >= 0; i--) {
        const wi = workItems[i];
        if (wi.DocumentId === documentId && wi.Source === source) {
            return i;
        }
    }
    return -1;
}

export type MissingSubidsCollectionType = {
    DocumentId: string;
    Source: string;
    Version: string;
    LatestVersion: string;
    ToCs: ToC;
    MissingSubIds: MasterDocumentSubId[];
};

export interface MasterDocumentSubId {
    SubId: string;
    OverrideAuthorsWithContract: any[];
    OverrideAuthors: any[];
    AuthorId: string;
    AuthorContractId: string;
    LegalFieldId: string;
    KarnovEditorId: string | null;
    ContentEditorId: string | null;
    SemanticPathId: string;
    IsAcademicReview: boolean;
    IsNotRelevant: boolean;
}

export interface MasterDocument {
    SubIds?: MasterDocumentSubId[];
    Errors: any[];
    Source: string;
    DocumentId: string;
    Version: string;
    LatestVersion: string;
    Created: string;
    CreatedBy: string | null;
    Updated: string;
    UpdatedBy: string | null;
    ToCs: ToC;
}

interface SubIdWrapper {
    SubId: string;
}

interface Reviewer {
    Id: string;
    DueDate: string | null;
    ReviewedDate: string | null;
    EnableOnRevision: string | null;
}

interface ReadyForReviewDate {
    DueDate: string | null;
    ChangedDate: string;
}

export interface WorkItem {
    Id: string;
    OrigId: string;
    SyncId: string;
    DocumentId: string;
    DocumentTitle: string;
    Version: string;
    Source: string;
    FromId: string | null;
    Status: string;
    AuthorEmail: string;
    AuthorName: string;
    SharepointPath: string;
    SharepointFolder: string;
    UnknownContentPath: string | null;
    UpdatedBy: string | null;
    AdminUpdatedBy: string;
    Created: string;
    Updated: string;
    SharedWith: any[];
    SubIds?: SubIdWrapper[];
    LegalFieldId: string;
    AuthorId: string;
    BackupPath: string | null;
    KarnovEditorId: string;
    DueDate: string;
    IsAcademicReview: boolean;
    ContentReviewer: Reviewer;
    ProofReader: Reviewer;
    EUReviewer: Reviewer | null;
    KarnovReviewer: Reviewer;
    IsEUReviewable: boolean;
    ReadyForReviewDate: ReadyForReviewDate | null;
    ReadyForPublishDate: string | null;
    PublishedDate: string | null;
    DeliveredDate: string | null;
    IsRevision: boolean;
    IsAuthorValidated: boolean;
    IsVersionUpgrade: boolean;
    IsDevOps: boolean;
    ToCs?: ToC;
    MissingSubIds?: MasterDocumentSubId[];
}

export interface ToC {
    DocumentId: string;
    Version: string;
    Source: string;
    Title: string;
    Children: ToCNode[];
    IsAssignable?: boolean;
    Id?: string;
}

interface ToCNode {
    Id: string;
    Title: string;
    Nodetype: string;
    NodeValue: string | null;
    IsExtendedToC: boolean;
    Children: ToCNode[];
    SemanticPath: string[];
    SemanticId: string;
    OriginalSemanticId: string | null;
}

export interface ResponsibleAreaResponseType {
    MasterDocuments: MasterDocument[];
    WorkItems: WorkItem[];
}
